import React, { createContext, ReactNode } from 'react';

// project imports
import defaultConfig, { MenuOrientation, ThemeMode, ThemeDirection } from 'config';
import useLocalStorage from 'hooks/useLocalStorage';
import { useSelector } from 'store';
import { fetchUpdateUserConfig } from 'store/slices/userConfig';

// types
import { CustomizationProps, FontFamily, I18n, PresetColor } from 'types/config';

// initial state
const initialState: CustomizationProps = {
  ...defaultConfig,
  onChangeMenuOrientation: () => {},
  onChangeMiniDrawer: () => {},
  onChangeMode: () => {},
  onChangePresetColor: () => {},
  onChangeLocale: () => {},
  onChangeDirection: () => {},
  onChangeContainer: () => {},
  onChangeFontFamily: () => {},
  onChangeBorderRadius: () => {},
  onChangeOutlinedField: () => {},
  onReset: () => {},
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
};

function ConfigProvider({ children }: ConfigProviderProps) {
  // ── סנכרון ערכת העיצוב עם פרופיל המשתמש (ProfileConfig.themeConfig) ──
  // שינוי שהמשתמש עושה כאן נשמר לשרת (debounce) ומשודר לקליינטים אחרים (תוסף/טאבים);
  // ערך שמגיע מהפרופיל מוחל מקומית. דגל dirty מבדיל שינוי-משתמש מהחלה חיצונית.
  const userDirtyRef = React.useRef(false);
  const persistTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const [config, setConfig] = useLocalStorage('berry-config-vite-ts', {
    menuOrientation: initialState.menuOrientation,
    miniDrawer: initialState.miniDrawer,
    fontFamily: initialState.fontFamily,
    borderRadius: initialState.borderRadius,
    outlinedFilled: initialState.outlinedFilled,
    mode: initialState.mode,
    presetColor: initialState.presetColor,
    i18n: initialState.i18n,
    themeDirection: initialState.themeDirection,
    container: initialState.container,
  });

  // החלה מהפרופיל: themeConfig שנשמר בשרת (ממכשיר/קליינט אחר) גובר על ה-localStorage המקומי
  const themeConfigFromProfile = useSelector((state) => state.userConfig?.themeConfig ?? null);
  React.useEffect(() => {
    if (!themeConfigFromProfile || typeof themeConfigFromProfile !== 'object') return;
    const merged = { ...config, ...(themeConfigFromProfile as Partial<typeof config>) };
    if (JSON.stringify(merged) === JSON.stringify(config)) return;
    userDirtyRef.current = false;
    setConfig(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeConfigFromProfile]);

  // שמירת שינוי-משתמש לפרופיל (debounce) — השרת ישדר userConfig:update לשאר הקליינטים
  React.useEffect(() => {
    if (!userDirtyRef.current) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      userDirtyRef.current = false;
      void fetchUpdateUserConfig({ themeConfig: config }, false).catch(() => {});
    }, 1200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const onChangeMenuOrientation = (menuOrientation: MenuOrientation) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      menuOrientation,
    });
  };

  const onChangeMiniDrawer = (miniDrawer: boolean) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      miniDrawer,
    });
  };

  const onChangeMode = (mode: ThemeMode) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      mode,
    });
  };

  const onChangePresetColor = (presetColor: PresetColor) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      presetColor,
    });
  };

  const onChangeLocale = (i18n: I18n) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      i18n,
    });
  };

  const onChangeDirection = (themeDirection: ThemeDirection) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      themeDirection,
    });
  };

  const onChangeContainer = (container: boolean) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      container,
    });
  };

  const onChangeFontFamily = (fontFamily: FontFamily) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      fontFamily,
    });
  };

  const onChangeBorderRadius = (event: Event, newValue: number | number[]) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      borderRadius: newValue as number,
    });
  };

  const onChangeOutlinedField = (outlinedFilled: boolean) => {
    userDirtyRef.current = true;
    setConfig({
      ...config,
      outlinedFilled,
    });
  };

  const onReset = () => {
    userDirtyRef.current = true;
    setConfig({ ...defaultConfig });
  };

  return (
    <ConfigContext.Provider
      value={{
        ...config,
        onChangeMenuOrientation,
        onChangeMiniDrawer,
        onChangeMode,
        onChangePresetColor,
        onChangeLocale,
        onChangeDirection,
        onChangeContainer,
        onChangeFontFamily,
        onChangeBorderRadius,
        onChangeOutlinedField,
        onReset,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export { ConfigProvider, ConfigContext };
